import clsx from 'clsx';
import { useEffect, useId, useMemo, useRef } from 'react';
import { ErrorMessage } from './error-message';

export type CheckboxProps = {
    label?: string | React.ReactNode;
    name?: string;
    cRef?: React.Ref<HTMLInputElement>;
    checked?: boolean;
    onChange: (checked: boolean) => void;
    value?: string | number;
    labelAlignment?: 'top' | 'center';
    type?: 'checkbox' | 'radio';
    disabled?: boolean;
    deselectable?: boolean;
    showRequiredError?: boolean;
    triggerChangeOnFocus?: boolean;
    brandColor?: string;
    labelFor?: boolean;
    errorMessage?: string;
    darkMode?: boolean;
};

export const Checkbox = ({
    onChange,
    label,
    cRef = useRef<HTMLInputElement>(null),
    checked,
    name,
    value,
    labelAlignment,
    type = 'checkbox',
    disabled = false,
    deselectable = false,
    showRequiredError = false,
    triggerChangeOnFocus = true,
    brandColor,
    errorMessage,
    darkMode = false,
}: CheckboxProps) => {
    const containerRef = useRef<HTMLLabelElement>(null);
    const htmlName = name ? name?.replace(/[^a-z0-9-_]/g, '').toLocaleLowerCase() : useId();

    const onChangeCallback = () => {
        if (disabled) {
            return;
        }

        onChange(type === 'radio' ? true : !checked);
    };

    useEffect(() => {
        containerRef.current?.addEventListener('keydown', keyDown);

        return () => {
            containerRef.current?.removeEventListener('keydown', keyDown);
        };
    });

    const brandStyle = useMemo(
        () =>
            brandColor
                ? {
                      accentColor: brandColor,
                  }
                : {},
        [brandColor]
    );

    const keyDown = (e: KeyboardEvent) => {
        if (e.key !== 'Enter') {
            return;
        }

        const target = e?.target as HTMLElement;
        if (target?.tagName?.toLowerCase() === 'a') {
            return;
        }

        e.preventDefault();
        e.stopPropagation();
        onChange(!checked);
    };

    return (
        <div className="r-space-y-1">
            <label
                ref={containerRef}
                data-component="checkbox"
                className={clsx('r-flex r-gap-4 r-rounded-lg r-p-1 r-transition-colors r-duration-300 r-ease-in-out hover:r-cursor-pointer', {
                    'hover:r-cursor-pointer': !disabled,
                    'r-items-center': labelAlignment === 'center',
                    'r-bg-red-100': showRequiredError,
                })}
            >
                <input
                    ref={cRef}
                    name={htmlName}
                    type={type}
                    value={value}
                    checked={checked}
                    disabled={disabled}
                    {...(showRequiredError && { 'aria-invalid': true })}
                    aria-describedby={showRequiredError && errorMessage ? `${htmlName}-error` : undefined}
                    onClick={() => {
                        if (type === 'radio' && deselectable && checked) {
                            onChange(false);
                        }
                    }}
                    onFocus={() => {
                        if (type === 'radio' && triggerChangeOnFocus) {
                            onChangeCallback();
                        }
                    }}
                    onChange={onChangeCallback}
                    className={clsx(
                        'r-h-6 r-w-6 r-flex-shrink-0 r-border-none r-accent-primary-300',
                        showRequiredError && 'r-ring-2 r-ring-red-700 r-ring-offset-2'
                    )}
                    style={brandStyle}
                />
                <span className={clsx('r-transition-colors', { 'r-text-white': darkMode && (!showRequiredError || !errorMessage) })}>{label}</span>
            </label>
            {showRequiredError && errorMessage && (
                <ErrorMessage id={`${htmlName}-error`} message={errorMessage} className="r-ml-0.5" darkMode={darkMode} />
            )}
        </div>
    );
};
